import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import DocumentPreviewModal from '../../../components/ui/DocumentPreviewModal';
import { syncExtraditionMlaDocumentsFromChecklist } from '../../../layouts/ExtraditionMlaRequestLayout';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import * as extraditionMlaService from '../../../services/extraditionMlaCaseRequests';
import {
  CASE_DOC_ACCEPT,
  CASE_DOC_DELETED_SUCCESS,
  validateCaseDocumentFile,
} from '../../../utils/caseDocumentUpload';
import {
  clearCriminalPreviewFile,
  setCriminalPreviewFile,
} from '../../../utils/criminalDocumentPreviewCache';
import { normalizeDocumentFileFields } from '../../../utils/fileDisplayName';
import { setFlashToast } from '../../../utils/flashToast';
import { focusFirstInvalid } from '../../../utils/requiredValidation';
import type { ExtraditionMlaDocumentRow } from '../../../types/extraditionMlaCaseRequest';
import { SectionCard } from '../WizardComponents';
import { DocumentUploadRow } from '../../../components/wizard/DocumentUploadRow';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaReviewPath, extraditionMlaWizardPath, ExtraditionMlaWizardShell } from './ExtraditionMlaWizardShell';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

export default function ExtraditionMlaStep5UploadDocumentsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<ExtraditionMlaDocumentRow | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingDocId = useRef<string | null>(null);

  useEffect(() => {
    if (!draft.fileCategoryId) {
      setLoadingDocs(false);
      return;
    }
    let mounted = true;
    setLoadingDocs(true);
    void extraditionMlaService.getDocumentChecklist(draft.fileCategoryId, draft.subFileCategoryId).then((r) => {
      if (!mounted) return;
      if (r.success) {
        setDraft((d) => ({
          ...d,
          documents: syncExtraditionMlaDocumentsFromChecklist(d.documents, r.data),
        }));
      } else {
        setFlashToast({ message: r.message ?? 'Failed to load document checklist.', variant: 'error' });
      }
      setLoadingDocs(false);
    });
    return () => {
      mounted = false;
    };
  }, [draft.fileCategoryId, draft.subFileCategoryId, setDraft]);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(6, draft);
    setErrors(nextErrors);
    if (!valid) {
      const reasonSpecs = draft.documents
        .filter((d) => nextErrors[`doc-reason-${d.id}`])
        .map((d) => ({
          key: `doc-reason-${d.id}`,
          label: 'Reason',
          value: '',
          elementId: `emla-doc-reason-${d.id}`,
        }));
      focusFirstInvalid(reasonSpecs, nextErrors);
    }
    return valid;
  };

  const goReview = () => {
    if (!validate()) return;
    nav(extraditionMlaReviewPath(draft.draftRequestId));
  };

  const openFilePicker = (docId: string) => {
    pendingDocId.current = docId;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const docId = pendingDocId.current;
    e.target.value = '';
    if (!file || !docId) return;

    const validation = validateCaseDocumentFile(file);
    if (!validation.ok) {
      setFlashToast({ message: validation.message, variant: 'error' });
      return;
    }

    setUploadingId(docId);
    setCriminalPreviewFile(docId, file);
    const r = await extraditionMlaService.uploadCaseFile(file);
    setUploadingId(null);
    if (!r.success) {
      clearCriminalPreviewFile(docId);
      setFlashToast({ message: r.message ?? 'File upload failed.', variant: 'error' });
      return;
    }

    const { fileName: storedKey, originalFileName: displayName } = normalizeDocumentFileFields(
      r.data.file_name ?? '',
      r.data.original_file_name ?? file.name,
    );
    setDraft((d) => ({
      ...d,
      documents: d.documents.map((doc) =>
        doc.id === docId
          ? {
              ...doc,
              fileName: storedKey,
              originalFileName: displayName || file.name,
              uploadTime: r.data.upload_time ?? new Date().toISOString(),
              skipReason: '',
            }
          : doc,
      ),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`doc-reason-${docId}`];
      return next;
    });
  };

  const requestRemoveFile = (docId: string) => {
    const doc = draft.documents.find((d) => d.id === docId);
    if (!doc?.fileName) return;
    setPendingDeleteDocId(docId);
    setDeleteConfirmOpen(true);
  };

  const confirmRemoveFile = () => {
    const docId = pendingDeleteDocId;
    if (!docId) {
      setDeleteConfirmOpen(false);
      return;
    }
    clearCriminalPreviewFile(docId);
    setDraft((d) => ({
      ...d,
      documents: d.documents.map((doc) =>
        doc.id === docId
          ? { ...doc, fileName: '', originalFileName: '', uploadTime: '', skipReason: '' }
          : doc,
      ),
    }));
    setDeleteConfirmOpen(false);
    setPendingDeleteDocId(null);
    setFlashToast({ message: CASE_DOC_DELETED_SUCCESS, variant: 'success' });
  };

  return (
    <ExtraditionMlaWizardShell currentStep={6} draft={draft}>
      <input
        ref={fileInputRef}
        type="file"
        accept={CASE_DOC_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => void onFileSelected(e)}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Document"
        message="Are you sure you want to delete? This action cannot be undone."
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteDocId(null);
        }}
        onConfirm={confirmRemoveFile}
      />

      <SectionCard title={f.sectionUploadDocuments}>
        {loadingDocs ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>{f.loadingChecklist}</p>
        ) : !draft.fileCategoryId ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>{f.selectCategoryForDocs}</p>
        ) : draft.documents.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>{f.noDocsConfigured}</p>
        ) : (
          draft.documents.map((doc) => (
            <DocumentUploadRow
              key={doc.id}
              idPrefix="emla"
              doc={doc}
              uploading={uploadingId === doc.id}
              reasonError={errors[`doc-reason-${doc.id}`]}
              onUploadClick={() => openFilePicker(doc.id)}
              onPreview={() => setPreviewDoc(doc)}
              onRemove={() => requestRemoveFile(doc.id)}
              onReasonChange={(reason: string) => {
                setDraft((d) => ({
                  ...d,
                  documents: d.documents.map((x) => (x.id === doc.id ? { ...x, skipReason: reason } : x)),
                }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next[`doc-reason-${doc.id}`];
                  return next;
                });
              }}
            />
          ))
        )}
      </SectionCard>

      <DocumentPreviewModal isOpen={Boolean(previewDoc)} doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={6}
        onBack={() => nav(extraditionMlaWizardPath(5, draft.draftRequestId))}
        showReview
        onNext={goReview}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
